/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItemButton, ListItemText,
  Paper, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { meAPI } from '../api/crm'
import { selectTenant } from '../store/slices/tenantSlice'
import { signOut } from '../store/slices/authSlice'
import { useFeatures, useIsPlatformAdmin, useMyTenants } from '../hooks/useCrmQueries'
import { getErrorMessage } from '../utils/apiErrors'
import Loading from '../components/common/Loading'

const SelectTenantPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useSelector((state) => state.auth.user)
  const { data: tenants, isLoading, error } = useMyTenants()
  const isPlatformAdmin = useIsPlatformAdmin()
  const features = useFeatures()
  const [searchParams] = useSearchParams()
  // ?create=1: arriving from sign-up (e.g. Google), so offer the new workspace straight away
  const [creating, setCreating] = useState(false)
  const canCreate = !!features.createWorkspaces
  const active = (tenants || []).filter((t) => t.status === 'ACTIVE')

  const choose = (tenant) => {
    dispatch(selectTenant(tenant))
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
    navigate('/', { replace: true })
  }

  // one workspace: no need to ask (unless they came to create one)
  const wantsNew = searchParams.get('create') === '1'
  useEffect(() => {
    if (tenants && active.length === 1 && tenants.length === 1 && !wantsNew) choose(active[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants])
  useEffect(() => {
    if (wantsNew && canCreate) setCreating(true)
  }, [wantsNew, canCreate])

  if (isLoading) return <Loading fullScreen />

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 460 }}>
        <Typography variant="h5">Choose a workspace</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Signed in as {user?.email}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(error)}</Alert>}
        {tenants && tenants.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You aren’t a member of any workspace yet. {canCreate
              ? 'Create your own (14 days free), or ask a workspace admin to invite ' + user?.email + '.'
              : `Ask a workspace admin to add ${user?.email}.`}
          </Alert>
        )}
        <List disablePadding>
          {(tenants || []).map((t) => (
            <ListItemButton
              key={t.tenantId}
              disabled={t.status !== 'ACTIVE'}
              onClick={() => choose(t)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
            >
              <ListItemText primary={t.name} secondary={t.role === 'TENANT_ADMIN' ? 'Admin' : 'Member'} />
              {t.status !== 'ACTIVE' ? <Chip label={t.status.toLowerCase()} /> : <ChevronRightIcon color="action" />}
            </ListItemButton>
          ))}
        </List>
        {canCreate && (
          <Button fullWidth variant={tenants?.length ? 'outlined' : 'contained'} startIcon={<AddIcon />} onClick={() => setCreating(true)} sx={{ mt: 1 }}>
            Create a workspace
          </Button>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          {isPlatformAdmin ? (
            <Button onClick={() => navigate('/platform/tenants')}>
              Manage tenants
            </Button>
          ) : <span />}
          <Button color="inherit" onClick={() => { dispatch(signOut()).then(() => { queryClient.clear(); navigate('/login') }) }}>
            Sign out
          </Button>
        </Box>
        {isPlatformAdmin && active.length === 0 && (
          <PlatformBootstrap />
        )}
      </Paper>
      {creating && <CreateWorkspaceDialog onClose={() => setCreating(false)} onCreated={choose} />}
    </Box>
  )
}

/** A workspace of your own, on a free trial, with you as its admin. */
const CreateWorkspaceDialog = ({ onClose, onCreated }) => {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: () => meAPI.createWorkspace(name.trim()),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['me'] })
      onCreated(tenant)
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); create.mutate() } }}>
      <DialogTitle>Create a workspace</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Free for 14 days, no card needed. You’ll be its admin and can invite your team and pick a ready-made template.
        </DialogContentText>
        {create.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(create.error)}</Alert>}
        <TextField fullWidth autoFocus required label="Company or workspace name" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={create.isPending || !name.trim()}>Create</Button>
      </DialogActions>
    </Dialog>
  )
}

/** A platform admin with no workspace yet can't enter the app shell, so offer tenant creation here. */
const PlatformBootstrap = () => {
  const navigate = useNavigate()
  return (
    <Alert severity="info" sx={{ mt: 2 }} action={<Button onClick={() => navigate('/platform/tenants')}>Create</Button>}>
      You’re a platform admin. Create the first workspace and make yourself its admin.
    </Alert>
  )
}

export default SelectTenantPage
